import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import api, { inr } from "@/lib/api";

export default function OrderSuccess() {
  const { orderId } = useParams();
  const { data: order, isLoading } = useQuery({
    queryKey: ["order", orderId],
    queryFn: async () => (await api.get(`/orders/${orderId}`)).data,
  });

  if (isLoading) {
    return <div className="flex min-h-screen items-center justify-center pt-16"><div className="h-10 w-10 animate-spin rounded-full border-4 border-sand border-t-leaf" /></div>;
  }

  if (!order) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 pt-16 text-center">
        <p className="font-display text-4xl font-extrabold text-forest">Order not found.</p>
        <Link to="/shop" className="rounded-full bg-forest px-6 py-3 text-sm font-bold text-cream hover:bg-leaf">Back to the squad</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 pb-24 pt-32 sm:px-6" data-testid="order-success-page">
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 14 }} className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-leaf/15">
        <CheckCircle2 className="h-10 w-10 text-leaf" />
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
        <h1 className="mt-6 text-center font-display text-4xl font-extrabold tracking-tight text-forest sm:text-5xl">
          ORDER IN. <span className="text-leaf">PANTRY LOADING.</span>
        </h1>
        <div className="mt-4 flex justify-center">
          <span
            data-testid="order-success-status"
            className={`rounded-full px-4 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] ${
              order.status === "paid" ? "bg-leaf/15 text-leaf" : "bg-forest/10 text-forest"
            }`}
          >
            {order.status === "paid" ? "Paid · Razorpay" : order.status === "pending_payment" ? "Awaiting payment" : "Confirmed · COD/UPI on delivery"}
          </span>
        </div>
        <p className="mt-3 text-center text-sm text-ink/60">
          Order <span data-testid="order-success-id" className="font-mono font-bold text-forest">{order.order_id}</span> is confirmed. We&apos;ll ring you on {order.customer.phone} before dispatch.
        </p>

        <div className="mt-10 rounded-3xl border border-sand bg-white p-6 sm:p-8">
          <ul className="space-y-4">
            {order.items.map((i) => (
              <li key={i.slug} className="flex items-center gap-3">
                <img src={i.image} alt={i.name} className="h-14 w-14 rounded-xl object-cover" />
                <div className="flex-1">
                  <p className="text-sm font-extrabold text-forest">{i.name}</p>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-ink/50">Qty {i.qty}</p>
                </div>
                <p className="font-mono text-sm font-bold text-forest">{inr(i.price * i.qty)}</p>
              </li>
            ))}
          </ul>
          <div className="mt-6 space-y-2 border-t border-sand pt-5 text-sm">
            <p className="flex justify-between text-ink/60"><span>Subtotal</span><span className="font-bold text-forest">{inr(order.subtotal)}</span></p>
            {order.bundle_discount > 0 && (
              <p className="flex justify-between text-leaf"><span className="font-semibold">Bundle saving ({order.bundle_rate}%)</span><span className="font-bold">-{inr(order.bundle_discount)}</span></p>
            )}
            {order.coupon_discount > 0 && (
              <p className="flex justify-between text-leaf" data-testid="order-success-coupon-row"><span className="font-semibold">{order.coupon_code}</span><span className="font-bold">-{inr(order.coupon_discount)}</span></p>
            )}
            <p className="flex justify-between text-ink/60"><span>Shipping</span><span className="font-bold text-forest">{order.shipping === 0 ? "Free" : inr(order.shipping)}</span></p>
            <p className="flex justify-between border-t border-sand pt-3 font-display text-xl font-extrabold text-forest"><span>Total ({order.payment_method === "cod" ? "COD" : order.payment_method === "razorpay" ? "Online" : "UPI"})</span><span data-testid="order-success-total">{inr(order.total)}</span></p>
          </div>
          <p className="mt-5 rounded-xl bg-leaf/10 px-4 py-3 text-xs font-semibold leading-relaxed text-forest">
            Delivering to: {order.customer.address}, {order.customer.city}, {order.customer.state} — {order.customer.pincode}. Arriving in 3–5 days.
          </p>
        </div>

        <div className="mt-8 text-center">
          <Link to="/shop" data-testid="order-success-continue" className="rounded-full bg-forest px-8 py-4 text-sm font-extrabold uppercase tracking-wide text-cream transition-colors hover:bg-leaf">
            Keep exploring
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
